import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { getOrderProgress } from 'shop/components/Order/utils'
import StatusProgress from './StatusProgress'
import DeliveryPin from './DeliveryPin'
import EtaDisplay from './EtaDisplay'
import { Order } from 'shop/types/cart'
import { SpinnerContainer } from '../commonStyles'
import Spinner from 'shop/components/Loader/Spinner'
import { useAppContent, useShop, useGoogleApis } from 'shop/hooks'
import { DELIVERY_FULFILLMENT } from 'shop/types'
import TrackMap from '../TrackMap'
import { useEffect, useState } from 'react'
import { differenceInMilliseconds } from 'date-fns'
import { LineBreak } from 'shop/components/Shop/commonStyles'
import { FulfillmentBreakdown } from 'shop/components/Cart/FulfillmentDetails/FulfillmentDetails'

const OVERDUE_THRESHOLD = 10 * 60 * 1000

const Banner = ({ order }: { order: Order | undefined }) => {
  const { logoImage, altLogoImage } = useAppContent()
  const logoUrl = altLogoImage || logoImage
  const { config, partner } = useShop()
  const { getGoogleApis, areGoogleApisLoaded } = useGoogleApis()
  const [isOverdue, setIsOverdue] = useState<boolean>(false)

  /** Load google APIs on load if libs not already loaded & order is trackable */
  useEffect(() => {
    if (order?.isTrackable && !areGoogleApisLoaded) {
      getGoogleApis()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.isTrackable])

  const isApplicableToOverdue =
    order?.delivery?.service === 'dispatch' &&
    order?.delivery?.status === 'searching'

  useEffect(() => {
    let overdueTimeout: NodeJS.Timeout | undefined

    if (!isApplicableToOverdue || !order.delivery.assignedPickupTime) {
      setIsOverdue(false)
      return
    }

    const timeDelta = differenceInMilliseconds(
      new Date(),
      new Date(order.delivery.assignedPickupTime)
    )

    setIsOverdue(timeDelta >= OVERDUE_THRESHOLD)

    if (timeDelta < OVERDUE_THRESHOLD) {
      const remainingTime = Math.abs(timeDelta - OVERDUE_THRESHOLD)

      overdueTimeout = setTimeout(() => {
        setIsOverdue(true)
      }, remainingTime)
    }

    return () => {
      clearTimeout(overdueTimeout)
    }
  }, [isApplicableToOverdue, order?.delivery?.assignedPickupTime])

  // handle loading state
  if (!order || !partner) {
    return (
      <Container>
        <SpinnerContainer>
          <Spinner size='40px' />
        </SpinnerContainer>
      </Container>
    )
  }

  const isDelivery = order.fulfillment.type === DELIVERY_FULFILLMENT
  const { code, message } = getOrderProgress(order, partner.name)

  const deliveryPin = order.delivery?.pincode

  const isRejected = order.status === 'REJECTED'

  return (
    <Container>
      <EtaContainer>
        <EtaDisplay order={order}></EtaDisplay>
      </EtaContainer>
      <ProgressContainer>
        {!isRejected && (
          <StatusProgress
            currentCode={code}
            isDelivery={isDelivery}
          ></StatusProgress>
        )}
        <Title>
          {isOverdue
            ? "Hang tight – we're doing our best to assign a driver. We'll let you know as soon as someone is on their way to collect your order."
            : message}
        </Title>
        {deliveryPin && <DeliveryPin pincode={deliveryPin}></DeliveryPin>}
        {order.isTrackable && areGoogleApisLoaded && (
          <MapContainer>
            <TrackMap
              config={config}
              destination={order.deliveryAddress}
              origin={order.store?.address}
              delivery={order.delivery}
              originLogoUrl={logoUrl}
            />
          </MapContainer>
        )}
        <LineBreak />
        <FulfillmentBreakdown
          fulfillment={order.fulfillment}
          deliveryAddress={order.deliveryAddress}
          storeAddress={order.store.address}
          dropoffNotes={order.dropoffNotes}
          isDelivery={isDelivery}
        />
      </ProgressContainer>
    </Container>
  )
}

const EtaContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({ display: 'flex', flexDirection: 'column' })
)

const ProgressContainer = styled.div(
  (): CSSObject => ({
    paddingTop: '24px',
    gap: '16px',
    display: 'flex',
    flexDirection: 'column'
  })
)

const Title = styled.p(
  (): CSSObject => ({
    fontSize: '16px',
    fontWeight: 600,
    color: '#262626',
    margin: '0 0 8px'
  })
)

const Container = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  color: 'black'
}))

const MapContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  width: '100%',
  height: '250px',
  borderRadius: theme.borderRadius,
  overflow: 'hidden',

  [theme?.mediaQueries?.viewport7]: { height: '400px' },

  // Remove border from Google Maps iframe
  '.gm-style iframe + div': { border: 'none!important;' }
}))

export default Banner
